import { useState, useMemo, useEffect } from 'react'
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { TableLoadingOverlay } from '@/components/ui/table-loading-overlay'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import {
  useGetTimesheetsQuery,
  type TimesheetEntry,
} from '@/redux/service/entries'
import { useGetProjectTypesQuery } from '@/redux/service/employee'
import {
  BanknoteX,
  LucideCircleDollarSign,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  FilterIcon,
} from 'lucide-react'
import { CustomPagination } from '@/components/CustomPagination'
import Tag from '@/lib/Tag'
import { useDebounce } from '@/lib/useDebounce'
import ReusableDateRangePicker, {
  type DateRange,
} from '@/components/ReusableDateRangePicker'
import { formatDate, formatSeconds, formatTime12 } from '@/lib/utils'
import { format } from 'date-fns'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useGetEmployeeListQuery,
  useSelectProjectsQuery,
  type SelectProject,
} from '@/redux/service/project'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ColumnVisibilityPopover } from '@/components/ColumnVisibilityPopover'
import { motion, AnimatePresence } from 'framer-motion'

const DEFAULT_COLUMNS = [
  { key: 'date', label: 'Date' },
  { key: 'project', label: 'Project' },
  { key: 'task', label: 'Task' },
  { key: 'employee', label: 'Employee' },
  { key: 'description', label: 'Description' },
  { key: 'billable', label: 'Billable' },
  { key: 'duration', label: 'Duration' },
  { key: 'task_estimate', label: 'Task Estimate' },
  { key: 'tags', label: 'Tags' },
  { key: 'start_time', label: 'Start Time' },
  { key: 'end_time', label: 'End Time' },
]

const DEFAULT_COLUMN_WIDTHS: Record<string, number> = {
  date: 130,
  project: 170,
  task: 260,
  employee: 150,
  description: 280,
  billable: 100,
  duration: 110,
  task_estimate: 130,
  tags: 140,
  start_time: 110,
  end_time: 110,
}

const MIN_COLUMN_WIDTH = 60

export default function TaskReports() {
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  })
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [search, setSearch] = useState('')
  const [employees, setEmployees] = useState('')
  const [projects, setProjects] = useState('')
  const [billable, setBillable] = useState<string>('all')
  const [projecttype, setProjecttype] = useState<string>('all')
  const debouncedSearch = useDebounce(search, 500)

  const [sortBy, setSortBy] = useState<string | undefined>()
  const [order, setOrder] = useState<'ASC' | 'DESC' | undefined>()
  const [filterCard, setFilterCard] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const [columns, setColumns] = useState<{ key: string; label: string }[]>(
    () => {
      const savedOrder = localStorage.getItem('taskreport-table-column-order')
      if (savedOrder) {
        try {
          const parsedOrder = JSON.parse(savedOrder)
          const known = parsedOrder
            .map((key: string) => DEFAULT_COLUMNS.find(c => c.key === key))
            .filter(Boolean) as { key: string; label: string }[]
          const missing = DEFAULT_COLUMNS.filter(
            c => !parsedOrder.includes(c.key)
          )
          return [...known, ...missing]
        } catch {
          // Fallback
        }
      }
      return DEFAULT_COLUMNS
    }
  )

  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    const savedVisibility = localStorage.getItem('taskreport-table-columns')
    if (savedVisibility) {
      try {
        const parsedVisibility = JSON.parse(savedVisibility) as string[]
        const missing = DEFAULT_COLUMNS.map(c => c.key).filter(
          key => !parsedVisibility.includes(key)
        )
        return [...parsedVisibility, ...missing]
      } catch {
        // Fallback
      }
    }
    return DEFAULT_COLUMNS.map(col => col.key)
  })

  useEffect(() => {
    localStorage.setItem(
      'taskreport-table-column-order',
      JSON.stringify(columns.map(c => c.key))
    )
  }, [columns])

  useEffect(() => {
    localStorage.setItem(
      'taskreport-table-columns',
      JSON.stringify(selectedColumns)
    )
  }, [selectedColumns])

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(
    () => {
      const savedWidths = localStorage.getItem('taskreport-table-column-widths')
      if (savedWidths) {
        try {
          return { ...DEFAULT_COLUMN_WIDTHS, ...JSON.parse(savedWidths) }
        } catch {
          // Fallback
        }
      }
      return DEFAULT_COLUMN_WIDTHS
    }
  )

  useEffect(() => {
    localStorage.setItem(
      'taskreport-table-column-widths',
      JSON.stringify(columnWidths)
    )
  }, [columnWidths])

  const handleColumnResizeStart = (key: string, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startWidth = columnWidths[key] ?? DEFAULT_COLUMN_WIDTHS[key] ?? 150

    const handleMouseMove = (moveEvent: MouseEvent) => {
      const delta = moveEvent.clientX - startX
      setColumnWidths(prev => ({
        ...prev,
        [key]: Math.max(MIN_COLUMN_WIDTH, startWidth + delta),
      }))
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setColumns(items => {
        const oldIndex = items.findIndex(i => i.key === active.id)
        const newIndex = items.findIndex(i => i.key === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const toggleColumn = (key: string) => {
    setSelectedColumns(prev =>
      prev.includes(key)
        ? prev.filter(c => c !== key && selectedColumns.length > 1)
        : [...prev, key]
    )
  }

  const isColumnVisible = (key: string) => selectedColumns.includes(key)

  const visibleColumnsCount = useMemo(() => {
    return columns.filter(col => selectedColumns.includes(col.key)).length
  }, [columns, selectedColumns])

  const handleSort = (column: string) => {
    if (sortBy === column) {
      if (order === 'ASC') {
        setOrder('DESC')
      } else if (order === 'DESC') {
        setSortBy(undefined)
        setOrder(undefined)
      }
    } else {
      setSortBy(column)
      setOrder('ASC')
    }
    setPage(1)
  }

  const getSortIcon = (column: string) => {
    if (sortBy !== column)
      return <ArrowUpDown className='w-3 h-3 ml-2 opacity-50 inline-block' />
    if (order === 'ASC')
      return <ArrowUp className='w-3 h-3 ml-2 inline-block' />
    return <ArrowDown className='w-3 h-3 ml-2 inline-block' />
  }
  const { data, isLoading, isFetching, currentData } = useGetTimesheetsQuery(
    {
      startDate: dateRange.from ? format(dateRange.from, 'yyyy-MM-dd') : '',
      endDate: dateRange.to ? format(dateRange.to, 'yyyy-MM-dd') : '',
      page,
      limit,
      search: debouncedSearch,
      employee: employees === 'all' ? '' : employees,
      project: projects === 'all' ? '' : projects,
      ...(billable !== 'all' && { billable: billable === 'true' }),
      projecttype: projecttype === 'all' ? '' : projecttype,
      sort_by: sortBy,
      order: order,
    },
    {
      skip:
        (!!dateRange.from && !dateRange.to) ||
        (!dateRange.from && !!dateRange.to),
      refetchOnMountOrArgChange: true,
    }
  )
  const { data: employeeList, isLoading: userLoading } =
    useGetEmployeeListQuery()
  const { data: allProjects, isLoading: projectLoading } =
    useSelectProjectsQuery(projecttype === 'all' ? undefined : { projecttype })
  const { data: projectTypesData, isLoading: projectTypesLoading } =
    useGetProjectTypesQuery()

  const totalPages = data?.pagination?.totalPages || 0
  const totalRecords = data?.pagination?.total || 0

  const clearFilters = () => {
    setDateRange({ from: undefined, to: undefined })
    setSearch('')
    setEmployees('')
    setProjects('')
    setBillable('all')
    setProjecttype('all')
    setSortBy(undefined)
    setOrder(undefined)
    setPage(1)
  }
  let tableBodyContent

  if (isLoading || isFetching) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={visibleColumnsCount} className='h-40' />
      </TableRow>
    )
  } else if ((currentData?.data?.length || 0) === 0) {
    tableBodyContent = (
      <TableRow>
        <TableCell colSpan={visibleColumnsCount} className='h-40 text-center'>
          <p className='text-muted-foreground text-sm font-medium'>
            No records found
          </p>
        </TableCell>
      </TableRow>
    )
  } else {
    tableBodyContent = currentData?.data.map((report: TimesheetEntry) => {
      const taskName =
        report.task_name && report.task_name.length > 60
          ? `${report.task_name.slice(0, 60)}...`
          : (report.task_name ?? '—')

      const description = report.description
        ? `${report.description.slice(0, 60)}...`
        : '—'
      return (
        <TableRow
          key={report.entry_id}
          className='group hover:bg-muted/50 border-b border-border/40 transition-colors'
        >
          {columns.map(column => {
            if (!isColumnVisible(column.key)) return null
            switch (column.key) {
              case 'date':
                return (
                  <TableCell
                    key={column.key}
                    className='truncate sticky left-0 z-10 bg-background font-medium text-center border-none'
                  >
                    {report.start_time ? formatDate(report.start_time) : '—'}
                    <div className='absolute right-0 top-0 bottom-0 w-[1px] bg-border/60 pointer-events-none' />
                  </TableCell>
                )
              case 'project':
                return (
                  <TableCell key={column.key} className='truncate'>
                    {report.project_name ?? '—'}
                  </TableCell>
                )
              case 'task':
                return (
                  <TableCell key={column.key} className='font-medium'>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className='block w-full truncate cursor-help font-normal'>
                          {taskName}
                        </span>
                      </TooltipTrigger>
                      {report.task_name && report.task_name.length > 20 && (
                        <TooltipContent className='max-w-[500px] bg-popover border text-popover-foreground p-2 rounded-md border-border shadow-xl'>
                          <p className='whitespace-normal break-words'>
                            {report.task_name}
                          </p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TableCell>
                )
              case 'employee':
                return (
                  <TableCell key={column.key} className='truncate'>
                    {report.user_name ?? '—'}
                  </TableCell>
                )
              case 'description':
                return (
                  <TableCell key={column.key} className='font-medium'>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className='block truncate cursor-help font-normal'>
                          {description}
                        </span>
                      </TooltipTrigger>
                      {report.description && report.description.length > 40 && (
                        <TooltipContent className='max-w-[500px] bg-popover border text-popover-foreground p-2 rounded-md border-border shadow-xl'>
                          <p className='whitespace-normal break-words'>
                            {report.description}
                          </p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TableCell>
                )
              case 'billable':
                return (
                  <TableCell key={column.key} className='truncate'>
                    {report.billable ? (
                      <LucideCircleDollarSign className='text-emerald-500' />
                    ) : (
                      <BanknoteX className='text-red-500' />
                    )}
                  </TableCell>
                )
              case 'duration':
                return (
                  <TableCell key={column.key} className='truncate'>
                    {report.duration_seconds
                      ? formatSeconds(report.duration_seconds) + 'h'
                      : '—'}
                  </TableCell>
                )
              case 'task_estimate':
                return (
                  <TableCell key={column.key} className='truncate'>
                    {report.task_time_estimate_ms
                      ? formatSeconds(report.task_time_estimate_ms / 1000) + 'h'
                      : '—'}
                  </TableCell>
                )
              case 'tags':
                return (
                  <TableCell
                    key={column.key}
                    className={`truncate ${report.tags ? 'text-start' : 'text-center'}`}
                  >
                    {report.tags ? <Tag text={report.tags.trim()} /> : '—'}
                  </TableCell>
                )
              case 'start_time':
                return (
                  <TableCell key={column.key} className='truncate'>
                    {formatTime12(report.start_time)}
                  </TableCell>
                )
              case 'end_time':
                return (
                  <TableCell key={column.key} className='truncate'>
                    {formatTime12(report.end_time)}
                  </TableCell>
                )
              default:
                return null
            }
          })}
        </TableRow>
      )
    })
  }
  return (
    <TooltipProvider>
      <div className='h-full flex flex-col space-y-4 p-2 overflow-hidden'>
        <div className='shrink-0 flex flex-col space-y-6'>
          <div className='flex items-center justify-between'>
            <h1 className='text-4xl font-extrabold tracking-tight text-foreground'>
              Timesheet
            </h1>
            <div className='flex items-center gap-3'>
              <div className='flex items-center gap-4'>
                <div className='relative flex-1 min-w-[220px]'>
                  <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500' />
                  <Input
                    id='search'
                    placeholder='Search by task, project, or user...'
                    value={search}
                    onChange={e => {
                      setSearch(e.target.value)
                      setPage(1)
                    }}
                    className='pl-10 h-10 border-border/50 text-foreground focus:border-primary transition-all'
                  />
                </div>

                <Select
                  value={billable}
                  onValueChange={value => {
                    setBillable(value)
                    setPage(1)
                  }}
                >
                  <SelectTrigger className='h-10 w-[150px] shrink-0 border-border/50 text-foreground rounded-lg'>
                    <SelectValue placeholder='All' />
                  </SelectTrigger>
                  <SelectContent className='bg-popover border-border/50'>
                    <SelectItem value='all'>Billable Status</SelectItem>
                    <SelectItem value='true'>Billable</SelectItem>
                    <SelectItem value='false'>Non-Billable</SelectItem>
                  </SelectContent>
                </Select>

                <div className='relative flex flex-col flex-1 min-w-[220px]'>
                  <ReusableDateRangePicker
                    value={dateRange}
                    onSelect={val => {
                      setDateRange(val)
                      setPage(1)
                    }}
                    className='rounded-lg h-10 flex items-center'
                  />
                  {((!!dateRange.to && !dateRange.from) ||
                    (!!dateRange.from && !dateRange.to)) && (
                    <p className='text-[10px] text-red-500 absolute -bottom-5 left-2 font-bold animate-pulse'>
                      COMPLETE RANGE REQUIRED
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={() => setFilterCard(prev => !prev)}
                variant='outline'
                size='sm'
                className='flex gap-2 h-8 text-xs border-border/50 hover:bg-accent/50 shrink-0'
              >
                <FilterIcon className='w-3 h-3' /> Filters
              </Button>
              <ColumnVisibilityPopover
                columns={columns}
                isColumnVisible={isColumnVisible}
                onToggle={toggleColumn}
                onReset={() => {
                  setColumns(DEFAULT_COLUMNS)
                  setSelectedColumns(DEFAULT_COLUMNS.map(c => c.key))
                  setColumnWidths(DEFAULT_COLUMN_WIDTHS)
                }}
                onDragEnd={handleDragEnd}
                sensors={sensors}
              />
            </div>
          </div>
        </div>
        <AnimatePresence>
          {filterCard && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className='shrink-0 pt-4 border-t border-border/20 space-y-4 overflow-hidden'
            >
              <div className='border border-border/60 rounded-xl p-3 shadow-sm space-y-5 bg-card'>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
                  <div className='space-y-1.5'>
                    <Label className='text-xs font-medium text-muted-foreground ml-1'>
                      Project Type
                    </Label>
                    <Select
                      value={projecttype}
                      onValueChange={value => {
                        setProjecttype(value)
                        setPage(1)
                      }}
                    >
                      <SelectTrigger className='h-10  border-border/50 text-foreground rounded-lg'>
                        <SelectValue placeholder='All Types' />
                      </SelectTrigger>
                      <SelectContent className='bg-popover border-border/50'>
                        <SelectItem value='all'>All Types</SelectItem>
                        {projectTypesLoading ? (
                          <SelectItem value='loading' disabled>
                            Loading...
                          </SelectItem>
                        ) : (
                          projectTypesData?.data?.map((type: string) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='space-y-1.5'>
                    <Label className='text-xs font-medium text-muted-foreground ml-1'>
                      Project
                    </Label>
                    <Select
                      value={projects}
                      onValueChange={value => setProjects(value)}
                    >
                      <SelectTrigger className='h-10  border-border/50 text-foreground rounded-lg'>
                        <SelectValue placeholder='All Projects' />
                      </SelectTrigger>
                      <SelectContent className='bg-popover border-border/50'>
                        <SelectItem value='all'>All Projects</SelectItem>
                        {projectLoading ? (
                          <SelectItem value='loading' disabled>
                            Loading...
                          </SelectItem>
                        ) : (
                          (allProjects?.data ?? []).map(
                            (project: SelectProject) => (
                              <SelectItem
                                key={project.project_id}
                                value={project.list_name}
                              >
                                {project.list_name}
                              </SelectItem>
                            )
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className='space-y-1.5'>
                    <Label className='text-xs font-medium text-muted-foreground ml-1'>
                      Active Employee
                    </Label>
                    <Select
                      value={employees}
                      onValueChange={value => setEmployees(value)}
                    >
                      <SelectTrigger className='h-10  border-border/50 text-foreground rounded-lg'>
                        <SelectValue placeholder='All Employees' />
                      </SelectTrigger>
                      <SelectContent className='bg-popover border-border/50'>
                        <SelectItem value='all'>All Employees</SelectItem>
                        {userLoading ? (
                          <SelectItem value='loading' disabled>
                            Loading...
                          </SelectItem>
                        ) : (
                          (employeeList?.data ?? []).map(
                            (user: { user_id: number; user_name: string }) => (
                              <SelectItem
                                key={user.user_id}
                                value={user.user_name}
                              >
                                {user.user_name}
                              </SelectItem>
                            )
                          )
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className='flex justify-end'>
                  <Button
                    variant='outline'
                    onClick={clearFilters}
                    size='sm'
                    className='h-8 text-xs border-border/50 hover:bg-accent/50'
                  >
                    Clear All Filters
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
        <div className='flex-1 relative rounded-sm border border-border/60 bg-card overflow-hidden'>
          <Table className='table-fixed [&_th]:h-10 [&_th]:px-4 [&_td]:px-4 [&_td]:py-2.5 border-none'>
            <colgroup>
              {columns
                .filter(column => isColumnVisible(column.key))
                .map(column => (
                  <col
                    key={column.key}
                    style={{
                      width:
                        columnWidths[column.key] ??
                        DEFAULT_COLUMN_WIDTHS[column.key] ??
                        150,
                    }}
                  />
                ))}
            </colgroup>
            <TableHeader>
              <TableRow className='border-b border-border/60  hover:bg-transparent'>
                {columns.map(column => {
                  if (!isColumnVisible(column.key)) return null
                  const sortKeyMap: Record<string, string> = {
                    date: 'start_time',
                    project: 'project_name',
                    task: 'task_name',
                    employee: 'user_name',
                    description: 'description',
                    duration: 'duration_seconds',
                  }
                  const sortKey = sortKeyMap[column.key]

                  const resizeHandle = (
                    <div
                      onMouseDown={e => handleColumnResizeStart(column.key, e)}
                      onClick={e => e.stopPropagation()}
                      className='absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none touch-none hover:bg-primary/50 active:bg-primary/70 z-30'
                    />
                  )

                  if (column.key === 'date') {
                    return (
                      <TableHead
                        key={column.key}
                        className='truncate sticky left-0 z-20 bg-muted font-medium text-center cursor-pointer select-none border-none'
                        onClick={() => handleSort('start_time')}
                      >
                        <div className='flex items-center justify-center gap-1'>
                          Date {getSortIcon('start_time')}
                        </div>
                        <div className='absolute right-0 top-0 bottom-0 w-[1px] bg-border/60 pointer-events-none' />
                        {resizeHandle}
                      </TableHead>
                    )
                  }

                  return (
                    <TableHead
                      key={column.key}
                      className={`relative font-medium ${sortKey ? 'cursor-pointer select-none' : ''}`}
                      onClick={sortKey ? () => handleSort(sortKey) : undefined}
                    >
                      <div className='flex items-center gap-1'>
                        {column.label} {sortKey && getSortIcon(sortKey)}
                      </div>
                      {resizeHandle}
                    </TableHead>
                  )
                })}
              </TableRow>
            </TableHeader>
            <TableBody>{tableBodyContent}</TableBody>
          </Table>
          {(isLoading || isFetching) && (
            <TableLoadingOverlay label='Loading Timesheet...' />
          )}
        </div>
        {(currentData?.data?.length ?? 0) > 0 && !isLoading && !isFetching && (
          <div className='shrink-0'>
            <CustomPagination
              limit={limit}
              setLimit={setLimit}
              page={page}
              setPage={setPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              title='Timesheet Records'
            />
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}
